import { useEffect } from 'react';

/** True when the user is typing, so shortcuts must stay out of the way. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

export interface HotkeyOptions {
  /** Suspend all shortcuts, e.g. while a modal owns the keyboard. */
  enabled?: boolean;
}

/**
 * Global single-key shortcuts.
 *
 * Deliberately plain keys rather than modifiers: during a show the operator is
 * working one-handed in the dark. They are suppressed whenever a field has
 * focus, so typing a presenter's name never fires a command.
 */
export function useHotkeys(
  map: Record<string, (e: KeyboardEvent) => void>,
  { enabled = true }: HotkeyOptions = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const handler = map[e.key];
      if (handler) {
        e.preventDefault();
        handler(e);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [map, enabled]);
}

/**
 * Undo, on the key everyone already presses for it.
 *
 * Separate from useHotkeys because that one deliberately ignores modifiers --
 * its shortcuts are single keys for one-handed use in the dark. Undo is the
 * exception worth making: Cmd/Ctrl+Z is so thoroughly muscle memory that
 * offering it under any other key would be the surprising choice.
 */
export function useUndoHotkey(run: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key.toLowerCase() !== 'z') return;
      // Let the browser handle undo inside a field the operator is typing in.
      if (isTypingTarget(e.target)) return;
      // Shift+Cmd+Z is redo elsewhere; we have no redo, so leave it alone
      // rather than silently doing an undo the operator did not ask for.
      if (e.shiftKey) return;
      e.preventDefault();
      run();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [run, enabled]);
}
